import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Scale } from "lucide-react";
import { api } from "../api/client";
import { theme } from "../theme";
import { usePlaybooks } from "../lib/playbooks";
import { PlaybookLink } from "./PlaybookLink";
import { PlaybookDecision } from "./PlaybookDecision";

// The row a register page carries under its title: a link to each playbook behind the page
// and, folded away until asked for, the decisions the guidance leaves to the person working
// here — each with the sources' words and the AI's recommendation for this organisation.

interface Option { id: string; question: string }

export function PlaybookBar({ ids }: { ids: string[] }) {
  const list = usePlaybooks();
  const available = ids.filter((id) => list?.some((p) => p.id === id));
  const [decisions, setDecisions] = useState<Array<{ playbook: string; option: Option }>>([]);
  const [open, setOpen] = useState(false);
  const key = available.join(",");

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    Promise.all(key.split(",").map((id) =>
      api.get<{ playbook: { options: Option[] } }>(`/help/playbooks/${encodeURIComponent(id)}`)
        .then((d) => d.playbook.options.map((option) => ({ playbook: id, option })))
        .catch(() => [] as Array<{ playbook: string; option: Option }>),
    )).then((all) => { if (!cancelled) setDecisions(all.flat()); });
    return () => { cancelled = true; };
  }, [key]);

  if (available.length === 0) return null;
  return (
    <div style={{ margin: "6px 0 10px" }}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
        {available.map((id) => <PlaybookLink key={id} id={id} />)}
        {decisions.length > 0 && (
          <button
            onClick={() => setOpen((o) => !o)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, fontWeight: 600, color: theme.color.accentText }}
            aria-expanded={open}
          >
            <Scale size={14} /> Decisions you make here ({decisions.length}) {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        )}
      </div>
      {open && decisions.map((d) => (
        <PlaybookDecision key={`${d.playbook}/${d.option.id}`} playbook={d.playbook} decision={d.option.id} />
      ))}
    </div>
  );
}
